import { Link } from './api.types';
import {
  FormErrorMessagePropsWithTestId,
  FormLabelPropsWithTestId,
  StringValidation,
  Time,
} from './general.types';
import { MultiItemResponse, SingleItemResponse } from './generic.types';
import { ProfileCard } from './profile';
import { SongStatusType } from './song';

export interface MessageActions {
  handleDelete: (message: MessageType) => void;
  handleRetry: (message: MessageType) => void;
}

export const CustomMessageType = 'newMessagesIndicator';

export interface MessageType {
  guid: string;
  user?: ProfileCard;
  needRetry: boolean;
  created: Time;
  updated: Time;
  text?: string;
  customType?: typeof CustomMessageType;
  deleted?: Time;
  createdBy?: {
    userGuid: string;
  };
  _links?: ChatMessageItem['_links'];
}

export interface ChatChannelUser {
  userGuid: string;
  profileCard: ProfileCard;
}

export interface FetchChannelEffectData {
  channelGuid: string;
  update?: boolean;
}

export enum ChatStatus {
  PENDING = 'PENDING',
  READY = 'READY',
}
export interface ChatChannel {
  guid: string;
  parentGuid: string;
  created: Time;
  updated: Time;
  channelUsers: ChatChannelUser[];
  unreadMessageCount: number;
  lastReadMessageDate: Time;
  status?: ChatStatus;
  _links: {
    getChanel: Link;
    updateMyChannelUser: Link;
    listMessages: Link;
    next?: Link;
  };
}

export interface CreateChatMessageValidation {
  properties: {
    text: StringValidation;
  };
}
export type MessageSendingType = 'UserMessage';

export interface ChatMessageItem {
  guid: string;
  refId?: string;
  channelGuid: string;
  type: MessageSendingType;
  text: string;
  createdBy: {
    userGuid: string;
  };
  created: Time;
  updated: Time;
  deleted?: Time;
  _links?: {
    createMessage: Link;
    updateMessage: Link;
    deleteMessage: Link;
  };
}

export interface ChatMessageUnreadCountChangedPayload {
  channelGuid: string;
  created: Time;
  updated: Time;
  lastReadMessageDate: Time;
  unreadMessageCount: number;
  userGuid: string;
}

export interface EditChatMessageItem {
  guid: string;
  channelGuid: string;
  type: MessageSendingType;
  createdBy: {
    userGuid: string;
  };
  created: Time;
  updated: Time;
  deleted?: Time;
}

export type UpdateLastSeenMessageResponse = SingleItemResponse<
  ChatMessageUnreadCountChangedPayload,
  'channelUser'
>;

export type GetSongChannelResponse = SingleItemResponse<ChatChannel, 'channel'>;

export type CreateChatMessageResponse = SingleItemResponse<
  ChatMessageItem,
  'message'
>;

export type DeleteChatMessageResponse = SingleItemResponse<
  EditChatMessageItem,
  'message'
>;

export type FetchChannelMessagesResponse = MultiItemResponse<
  ChatMessageItem,
  'message',
  { next?: Link }
>;

export type FetchChatUnreadCountResponse = SingleItemResponse<
  { hasUnreadMessages: boolean },
  'unread'
>;

export interface ChatChannelListItem {
  guid: string;
  parentGuid: string;
  title: string;
  icon: string;
  status: SongStatusType;
  unreadMessageCount: number;
  latestMessage?: {
    guid: string;
    type: string;
    text: string;
    updated: Time;
    createdBy: {
      profileCard?: ProfileCard;
      userGuid: string;
    };
  };
  created: Time;
  updated: Time;
}

export interface ChatChannelLinks {
  getChannel?: Link;
  next?: Link;
  nextLimit?: Link;
}

export type FetchChannelsResponse = MultiItemResponse<
  ChatChannelListItem,
  'channel',
  ChatChannelLinks
>;

export interface ChatInputProps {
  placeholder?: string;
  label?: string;
  labelProps?: FormLabelPropsWithTestId;
  errorMessage?: string;
  errorMessageProps?: FormErrorMessagePropsWithTestId;
}

export interface GroupsInfo {
  counts: number[];
  titles: string[];
  firstItemsIndexes: number[];
}
